import { perf } from './PerfDiag';
import type { PerfGpuTimer } from './GpuTimer';
import {
  PERF_TOGGLE_PATHS,
  getPerfToggle,
  setPerfToggle,
  type PerfTogglePath,
} from './PerfToggles';

export class PerfDiagPanel {
  private readonly el: HTMLDivElement;
  private readonly phaseTable: HTMLDivElement;
  private readonly gpuTable: HTMLDivElement;
  private readonly stateEl: HTMLDivElement;
  private readonly eventsEl: HTMLDivElement;
  private readonly gpuTimer: PerfGpuTimer | null;

  constructor(_parent: HTMLElement, gpuTimer: PerfGpuTimer | null = null) {
    this.gpuTimer = gpuTimer;
    const el = document.createElement('div');
    el.style.cssText = `
      position: fixed; top: 8px; left: 8px; z-index: 2147483647;
      width: min(520px, calc(100vw - 16px)); max-height: calc(100vh - 16px);
      overflow: auto; box-sizing: border-box;
      font: 11px/1.35 ui-monospace, Menlo, Consolas, monospace;
      background: rgba(2, 7, 11, 0.86); color: #d8f7ff; padding: 10px;
      border: 1px solid rgba(92, 193, 218, 0.55); border-radius: 4px;
      box-shadow: 0 12px 28px rgba(0,0,0,0.35);
    `;

    const title = document.createElement('div');
    title.style.cssText = 'font-weight: 700; color: #ffffff; margin-bottom: 8px;';
    title.textContent = 'PerfDiag';
    el.appendChild(title);

    const toggles = document.createElement('div');
    toggles.style.cssText = `
      display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 4px 10px; margin-bottom: 10px;
    `;
    for (const path of PERF_TOGGLE_PATHS) toggles.appendChild(this.toggleRow(path));
    el.appendChild(toggles);

    this.stateEl = document.createElement('div');
    this.stateEl.style.cssText = 'white-space: pre; margin-bottom: 10px;';
    el.appendChild(this.stateEl);

    this.phaseTable = document.createElement('div');
    this.phaseTable.style.cssText = 'white-space: pre; margin-bottom: 10px;';
    el.appendChild(this.phaseTable);

    this.gpuTable = document.createElement('div');
    this.gpuTable.style.cssText = 'white-space: pre; margin-bottom: 10px;';
    el.appendChild(this.gpuTable);

    this.eventsEl = document.createElement('div');
    this.eventsEl.style.cssText = 'white-space: pre;';
    el.appendChild(this.eventsEl);

    // Mount on body (outside the canvas parent's stacking context) so the
    // panel always sits above the in-game UI overlays.
    document.body.appendChild(el);
    this.el = el;
  }

  tick(): void {
    const snapshot = perf.snapshot();
    const state = snapshot.state;
    this.stateEl.textContent =
      `frame ${snapshot.frameDtMs.toFixed(2)}ms  uncategorized ${snapshot.uncategorizedMs.toFixed(2)}ms\n` +
      (state
        ? `jerk ${state.jerk.toFixed(1)}  dY ${state.dY.toFixed(3)}  dCam ${state.dCamera.toFixed(3)}\n` +
          `ground ${fmt(state.groundY)}  kerb ${fmt(state.kerbY)}`
        : 'state pending');

    const rows = Object.entries(snapshot.phaseStats)
      .sort(([, a], [, b]) => b.p95 - a.p95)
      .slice(0, 14)
      .map(([phase, stats]) => {
        const bar = '#'.repeat(Math.min(20, Math.round(stats.p95)));
        return `${phase.padEnd(18)} mean ${stats.mean.toFixed(2).padStart(6)}  p95 ${stats.p95.toFixed(2).padStart(6)}  max ${stats.max.toFixed(2).padStart(6)}  ${bar}`;
      });
    this.phaseTable.textContent = ['phase timings (CPU ms)', ...rows].join('\n');

    if (this.gpuTimer) {
      if (!this.gpuTimer.available()) {
        this.gpuTable.textContent = 'gpu timings  EXT_disjoint_timer_query_webgl2 unavailable\n  (try a non-incognito window if results stay empty)';
      } else {
        const gpuStats = this.gpuTimer.stats();
        const gpuRows = Object.entries(gpuStats)
          .sort(([, a], [, b]) => b.p95 - a.p95)
          .map(([phase, s]) => {
            const bar = '#'.repeat(Math.min(20, Math.round(s.p95)));
            return `${phase.padEnd(18)} mean ${s.mean.toFixed(2).padStart(6)}  p95 ${s.p95.toFixed(2).padStart(6)}  max ${s.max.toFixed(2).padStart(6)}  ${bar}`;
          });
        const total = this.gpuTimer.totalMean();
        const header = `gpu timings (GPU ms)  total ${total.toFixed(2)}ms`;
        this.gpuTable.textContent = gpuRows.length > 0
          ? [header, ...gpuRows].join('\n')
          : `${header}\n  (warming up — results lag 1–3 frames)`;
      }
    } else {
      this.gpuTable.textContent = '';
    }

    const events = snapshot.events.slice(0, 10).map((event) => {
      const data = event.data ? ` ${JSON.stringify(event.data)}` : '';
      return `${event.kind}${data}`;
    });
    this.eventsEl.textContent = ['events', ...events].join('\n');
  }

  destroy(): void {
    this.el.remove();
  }

  private toggleRow(path: PerfTogglePath): HTMLElement {
    const label = document.createElement('label');
    label.style.cssText = 'display: flex; align-items: center; gap: 6px; min-width: 0;';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = getPerfToggle(path);
    input.addEventListener('change', () => {
      setPerfToggle(path, input.checked);
    });
    const text = document.createElement('span');
    text.textContent = path;
    text.style.cssText = 'overflow: hidden; text-overflow: ellipsis;';
    label.append(input, text);
    return label;
  }
}

function fmt(value: number | undefined): string {
  return typeof value === 'number' ? value.toFixed(3) : 'n/a';
}
